import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Sparkles } from "lucide-react";
import ImageUploader from "../components/ImageUploader";
import VinScanner from "../components/VinScanner";
import HostCalendarPanel from "../components/HostCalendarPanel";
import VehicleInspectionCapture, {
  inspectionPhotosComplete,
  inspectionPhotosPayload,
} from "../components/VehicleInspectionCapture";
import { formatBookingError, formatInspectionSubmitError } from "../lib/bookingErrors";
import { AI_NAME } from "../lib/brand";
import { formatMoney, priceSuggestionCurrency } from "../lib/currencyDisplay";
import { VEHICLE_BODY_TYPES } from "../lib/vehicleTypes";
import { needsFreshBaselineCapture, requiresFreshAiImages } from "../lib/vehicleRejection";

const EDITABLE_STATUSES = new Set(["pending", "approved", "paused", "rejected"]);

export default function EditVehicle() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [baselineStale, setBaselineStale] = useState(false);
  const [freshAiImagesRequired, setFreshAiImagesRequired] = useState(true);
  const [conditionPhotos, setConditionPhotos] = useState({});

  useEffect(() => {
    // Don't fetch until AuthContext has resolved — avoids anonymous-user 404s
    // on pending/rejected listings that aren't visible to the public.
    if (authLoading) return;

    (async () => {
      try {
        let v;
        if (user?.is_admin) {
          // Admin endpoint bypasses _vehicle_visible_to_public and works for
          // any status (pending, rejected, etc.).
          const r = await axios.get(`${API}/admin/vehicles/${id}/review`, { withCredentials: true });
          v = r.data.vehicle;
        } else {
          const r = await axios.get(`${API}/vehicles/${id}`, { withCredentials: true });
          v = r.data;
        }
        if (!user?.is_admin && v.owner_id !== user?.user_id) {
          toast.error("You don't have permission to edit this listing");
          navigate("/host?tab=cars");
          return;
        }
        if (!EDITABLE_STATUSES.has(v.status)) {
          toast.error("This listing cannot be edited in its current state");
          navigate("/host?tab=cars");
          return;
        }
        setStatus(v.status);
        setRejectionReason(v.rejection_reason || "");
        setBaselineStale(Boolean(v.baseline_stale));
        setFreshAiImagesRequired(requiresFreshAiImages(v));
        setForm({
          make: v.make || "",
          model: v.model || "",
          trim: v.trim || "",
          year: v.year || 2023,
          vehicle_type: v.vehicle_type || "sedan",
          transmission: v.transmission || "automatic",
          powertrain: v.powertrain || "gasoline",
          seats: v.seats || 5,
          location_city: v.location_city || "",
          location_address: v.location_address || "",
          daily_rate: v.daily_rate ?? 199,
          exterior_color: v.exterior_color || "",
          interior_color: v.interior_color || "",
          allows_one_way: !!v.allows_one_way,
          allows_return_date_extension: !!v.allows_return_date_extension,
          allows_return_address_change: !!v.allows_return_address_change,
          one_way_fee: v.one_way_fee ?? 30,
          one_way_fee_per_km: v.one_way_fee_per_km ?? "",
          one_way_fee_max: v.one_way_fee_max ?? "",
          included_km_per_day: v.included_km_per_day ?? 320,
          overage_fee_per_km: v.overage_fee_per_km ?? 0.16,
          delivery_lead_days: v.delivery_lead_days ?? "",
          operating_open: v.operating_open || "",
          operating_close: v.operating_close || "",
          after_hours_fee: v.after_hours_fee ?? "",
          vin: v.vin || "",
          registration_number: v.registration_number || "",
          description: v.description || "",
          features: (v.features || []).join(", "),
          images: v.images || [],
        });
      } catch (e) {
        const httpStatus = e.response?.status;
        if (httpStatus === 404) {
          toast.error("Listing not found");
        } else if (httpStatus === 403) {
          toast.error("You don't have permission to edit this listing");
        } else {
          toast.error(e.response?.data?.detail || "Could not load listing");
        }
        navigate("/host?tab=cars");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user, authLoading, navigate]);

  const geocodeAddress = async (address) => {
    const raw = (address || "").trim();
    if (!raw) return;
    try {
      const r = await axios.get(`${API}/geocode`, { params: { address: raw }, withCredentials: true });
      if (r.data?.location_city) {
        setForm((f) => ({ ...f, location_city: r.data.location_city }));
      }
    } catch {
      /* best-effort */
    }
  };

  const resolveCity = () => {
    if ((form?.location_city || "").trim()) return form.location_city.trim();
    const parts = (form?.location_address || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 3) return parts[parts.length - 2];
    if (parts.length >= 2) return parts[parts.length - 1];
    if (parts.length === 1) return parts[0];
    return "";
  };
  const locationForCurrency = {
    city: [form?.location_city, form?.location_address].filter(Boolean).join(" "),
  };
  const localPriceCurrency = priceSuggestionCurrency(null, locationForCurrency);
  const suggestionCurrency = priceSuggestionCurrency(suggestion, locationForCurrency);

  const buildPayload = () => {
    const vin = (form.vin || "").trim().toUpperCase();
    const reg = (form.registration_number || "").trim().toUpperCase();
    return {
      ...form,
      vin,
      registration_number: reg,
      images: form.images,
      year: parseInt(form.year, 10),
      seats: parseInt(form.seats, 10),
      daily_rate: parseFloat(form.daily_rate),
      one_way_fee: parseFloat(form.one_way_fee),
      one_way_fee_per_km: form.one_way_fee_per_km !== "" && form.one_way_fee_per_km != null ? parseFloat(form.one_way_fee_per_km) : null,
      one_way_fee_max: form.one_way_fee_max !== "" && form.one_way_fee_max != null ? parseFloat(form.one_way_fee_max) : null,
      included_km_per_day: parseInt(form.included_km_per_day, 10),
      overage_fee_per_km: parseFloat(form.overage_fee_per_km),
      delivery_lead_days: form.delivery_lead_days ? parseInt(form.delivery_lead_days, 10) : null,
      operating_open: form.operating_open || null,
      operating_close: form.operating_close || null,
      after_hours_fee: form.after_hours_fee !== "" && form.after_hours_fee != null ? parseFloat(form.after_hours_fee) : null,
      features: form.features.split(",").map((s) => s.trim()).filter(Boolean),
    };
  };

  const submit = async (e, resubmit = false) => {
    e.preventDefault();
    if (!form) return;
    const vin = (form.vin || "").trim().toUpperCase();
    if (!/^[A-HJ-NPR-Z0-9]{11,17}$/.test(vin)) {
      toast.error("Enter a valid VIN (11–17 characters, letters and numbers only)");
      return;
    }
    const reg = (form.registration_number || "").trim().toUpperCase();
    if (!reg || reg.length < 2) {
      toast.error("Enter your car number plate");
      return;
    }
    if (!(form.location_address || "").trim()) {
      toast.error("Enter the full street address where the car is kept");
      return;
    }
    if ((form.images || []).length === 0) {
      toast.error("Add at least one listing photo");
      return;
    }
    const needsBaselineScan = needsFreshBaselineCapture({
      status,
      baselineStale,
      freshAiImagesRequired,
    });
    if (needsBaselineScan && !inspectionPhotosComplete(conditionPhotos)) {
      toast.error("Complete all 6 AI condition photos before resubmitting");
      return;
    }
    setSubmitting(true);
    try {
      await axios.patch(`${API}/vehicles/${id}`, buildPayload(), { withCredentials: true });
      if (needsBaselineScan) {
        await axios.post(`${API}/inspections`, {
          phase: "listing_baseline",
          vehicle_id: id,
          photos: inspectionPhotosPayload(conditionPhotos),
        }, { withCredentials: true });
        setBaselineStale(false);
      }
      if (resubmit || status === "rejected") {
        await axios.post(`${API}/vehicles/${id}/resubmit`, {}, { withCredentials: true });
        toast.success(needsBaselineScan
          ? "Listing updated, baseline refreshed, and resubmitted for review"
          : "Listing updated, existing evidence reassessed, and resubmitted for review");
      } else if (status === "approved" || status === "paused") {
        toast.success("Changes saved — listing is back in the admin review queue");
      } else {
        toast.success("Listing updated");
      }
      navigate("/host?tab=cars");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "vin_already_listed") toast.error("This VIN is already listed on Z-Cars");
      else if (d === "listing_not_rejected") toast.error("Only rejected listings can be resubmitted");
      else if (d === "baseline_rescan_required") {
        // The backend can escalate a reuse decision when stored evidence is
        // missing or unusable; immediately reveal the secure capture flow.
        setBaselineStale(true);
        setFreshAiImagesRequired(true);
        toast.error(formatBookingError(d));
      }
      else if (d === "location_address_required") toast.error(formatBookingError(d));
      else if (Array.isArray(d)) toast.error(d[0]?.msg || "Invalid vehicle details");
      else toast.error(formatInspectionSubmitError(d) || formatBookingError(d) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const aiSuggest = async () => {
    if (!form) return;
    const year = parseInt(form.year, 10);
    const city = resolveCity();
    if (!form.make?.trim() || !form.model?.trim() || !city) {
      toast.error("Fill make, model, and street address first");
      return;
    }
    if (!Number.isFinite(year) || year < 1980 || year > new Date().getFullYear() + 1) {
      toast.error("Enter a valid year before requesting a price suggestion");
      return;
    }
    setSuggesting(true);
    try {
      const r = await axios.post(`${API}/pricing/suggest`, {
        kind: "vehicle",
        make: form.make.trim(),
        model: form.model.trim(),
        trim: form.trim?.trim() || undefined,
        year,
        vehicle_type: form.vehicle_type,
        transmission: form.transmission,
        city,
      }, { withCredentials: true });
      setSuggestion(r.data);
      setForm((f) => ({ ...f, daily_rate: r.data.suggested_rate }));
      toast.success(`Suggested ${formatMoney(r.data.suggested_rate, r.data.currency)}/day (${r.data.source || "estimate"})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not get suggestion");
    } finally {
      setSuggesting(false);
    }
  };

  if (loading || !form) {
    return <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12 text-[var(--zc-text-2)]" data-testid="edit-vehicle-loading">Loading…</div>;
  }

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12" data-testid="edit-vehicle-page">
      <div className="eyebrow mb-2">Host</div>
      <h1 className="zc-headline text-4xl md:text-5xl">Edit listing</h1>
      <p className="text-[var(--zc-text-2)] mt-3">
        {status === "approved" || status === "paused"
          ? "Saving changes to a live listing sends it back to admin for re-approval before it appears in search again."
          : "Update your vehicle details."}
      </p>

      {(status === "approved" || status === "paused") && (
        <div className="mt-6 p-4 rounded-lg border border-[var(--zc-border)] bg-[var(--zc-warn,#f59e0b)]/10" data-testid="ev-rereview-banner">
          <div className="font-semibold text-[var(--zc-text)]">Live listing</div>
          <p className="text-sm text-[var(--zc-text-2)] mt-1">Material edits require admin re-approval. Your listing is hidden from search while pending review.</p>
        </div>
      )}

      {status === "rejected" && (
        <div className="mt-6 p-4 rounded-lg border border-[var(--zc-border)] bg-[var(--zc-danger)]/10" data-testid="ev-rejected-banner">
          <div className="font-semibold text-[var(--zc-text)]">Listing rejected</div>
          <p className="text-sm text-[var(--zc-text-2)] mt-1">
            {rejectionReason
              ? <>Reason: {rejectionReason}. </>
              : null}
            {freshAiImagesRequired
              ? "Update the listing, complete the required fresh AI baseline scan, then tap Save & resubmit."
              : "Update the listing, then resubmit. Existing image evidence will be reassessed; a fresh scan is only required if that evidence is missing or unusable."}
          </p>
        </div>
      )}

      {needsFreshBaselineCapture({ status, baselineStale, freshAiImagesRequired }) && (
        <div className="mt-8 zc-card p-5 md:p-6" data-testid="ev-baseline-rescan">
          <div className="eyebrow mb-1">AI condition baseline</div>
          <h2 className="font-semibold text-[var(--zc-text)]">Fresh 6-angle scan required</h2>
          <p className="text-sm text-[var(--zc-text-2)] mt-2">
            Capture front, rear, both sides, interior, and odometer so our AI can verify colors and condition match your updated listing.
          </p>
          <div className="mt-4">
            <VehicleInspectionCapture
              value={conditionPhotos}
              onChange={setConditionPhotos}
              inspectionPhase="listing_baseline"
              testid="ev-baseline-capture"
            />
          </div>
        </div>
      )}

      <form onSubmit={(e) => submit(e, false)} className="space-y-6 mt-10" data-testid="edit-vehicle-form">
        <div className="zc-card p-5 md:p-6">
          <div className="eyebrow mb-1">Number plate &amp; VIN</div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <Field label="Number plate" testid="ev-registration" v={form.registration_number} on={(v) => setForm((f) => ({ ...f, registration_number: v.toUpperCase() }))} required mono maxLength={20} />
            <Field label="VIN" testid="ev-vin" v={form.vin} on={(v) => setForm((f) => ({ ...f, vin: v.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, "").slice(0, 17) }))} required mono maxLength={17} />
          </div>
          <VinScanner
            testid="ev-vin-scanner"
            onConfirm={(vin) => setForm((f) => ({ ...f, vin }))}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Make" testid="ev-make" v={form.make} on={(v) => setForm((f) => ({ ...f, make: v }))} required />
          <Field label="Model" testid="ev-model" v={form.model} on={(v) => setForm((f) => ({ ...f, model: v }))} required />
          <Field label="Trim" testid="ev-trim" v={form.trim} on={(v) => setForm((f) => ({ ...f, trim: v }))} />
          <Field label="Year" type="number" testid="ev-year" v={form.year} on={(v) => setForm((f) => ({ ...f, year: v }))} />
          <Field label={`Daily rate (${localPriceCurrency})`} type="number" testid="ev-rate" v={form.daily_rate} on={(v) => setForm((f) => ({ ...f, daily_rate: v }))} />
          <SelectField label="Body type" testid="ev-type" v={form.vehicle_type} on={(v) => setForm((f) => ({ ...f, vehicle_type: v }))} options={VEHICLE_BODY_TYPES} />
          <SelectField label="Powertrain" testid="ev-powertrain" v={form.powertrain} on={(v) => setForm((f) => ({ ...f, powertrain: v }))} options={[
            ["gasoline", "Gasoline"], ["electric", "Electric"], ["phev", "Plug-in hybrid"], ["hybrid", "Hybrid"],
          ]} />
          <SelectField label="Transmission" testid="ev-trans" v={form.transmission} on={(v) => setForm((f) => ({ ...f, transmission: v }))} options={[["automatic", "Automatic"], ["manual", "Manual"]]} />
          <Field label="Exterior color" testid="ev-ext-color" v={form.exterior_color} on={(v) => setForm((f) => ({ ...f, exterior_color: v }))} />
          <Field label="Interior color" testid="ev-int-color" v={form.interior_color} on={(v) => setForm((f) => ({ ...f, interior_color: v }))} />
          <Field label="Seats" type="number" testid="ev-seats" v={form.seats} on={(v) => setForm((f) => ({ ...f, seats: v }))} />
          <Field
            label="Full address"
            testid="ev-address"
            v={form.location_address}
            on={(v) => setForm((f) => ({ ...f, location_address: v }))}
            onBlur={() => geocodeAddress(form.location_address)}
            required
            placeholder="123 Main St, Unit 4, Toronto, ON"
            hint="Full address where renters pick up the car"
          />
          <Field
            label="City"
            testid="ev-city"
            v={form.location_city}
            on={(v) => setForm((f) => ({ ...f, location_city: v }))}
            placeholder="Auto-filled from address"
            hint="Auto-filled from address"
            readOnly={!!form.location_city && !!(form.location_address || "").trim()}
          />
          <Field label="Delivery lead (days, optional)" type="number" testid="ev-delivery" v={form.delivery_lead_days} on={(v) => setForm((f) => ({ ...f, delivery_lead_days: v }))} placeholder="e.g. 1" hint="How many days before the car can be ready for pickup" />
        </div>

        <div className="zc-card p-5 md:p-6">
          <div className="eyebrow mb-1">Trip policies</div>
          <p className="text-xs text-[var(--zc-text-3)] mb-4">Choose which changes renters may request after booking. Legacy listings remain opted out until you enable them.</p>
          <div className="space-y-6">
            <div>
              <div className="text-xs font-semibold text-[var(--zc-text-2)] mb-3">Changes after booking</div>
              <div className="space-y-3">
                <PolicyCheckbox
                  checked={form.allows_return_date_extension}
                  onChange={(checked) => setForm((f) => ({ ...f, allows_return_date_extension: checked }))}
                  testid="ev-return-extension"
                  label="Allow renters to request a later return date"
                  hint="The current return can never be shortened."
                />
                <PolicyCheckbox
                  checked={form.allows_return_address_change}
                  onChange={(checked) => setForm((f) => ({ ...f, allows_return_address_change: checked }))}
                  testid="ev-return-address-change"
                  label="Allow renters to request a different return address"
                  hint={form.allows_one_way ? "Different addresses must still pass the one-way distance check." : "Enable one-way trips below for returns to a different address."}
                />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-[var(--zc-text-2)] mb-3">One-way trips</div>
              <div className="grid md:grid-cols-2 gap-4">
                <label className="flex items-center gap-2 text-sm md:col-span-2">
                  <input type="checkbox" checked={form.allows_one_way} onChange={(e) => setForm((f) => ({ ...f, allows_one_way: e.target.checked }))} className="w-4 h-4 accent-[var(--zc-accent)]" data-testid="ev-one-way" />
                  Allow different return location (one-way trips)
                </label>
                <Field label={`Base one-way fee (${localPriceCurrency})`} type="number" testid="ev-one-way-fee" v={form.one_way_fee} on={(v) => setForm((f) => ({ ...f, one_way_fee: v }))} hint="Minimum fee before distance" />
                <Field label={`${localPriceCurrency}/km one-way surcharge`} type="number" testid="ev-one-way-km" v={form.one_way_fee_per_km} on={(v) => setForm((f) => ({ ...f, one_way_fee_per_km: v }))} placeholder="0.75" />
                <Field label={`Max one-way fee (${localPriceCurrency})`} type="number" testid="ev-one-way-max" v={form.one_way_fee_max} on={(v) => setForm((f) => ({ ...f, one_way_fee_max: v }))} placeholder="250" />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-[var(--zc-text-2)] mb-3">Distance allowance</div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Included km per day" type="number" testid="ev-km-day" v={form.included_km_per_day} on={(v) => setForm((f) => ({ ...f, included_km_per_day: v }))} hint="Included daily mileage before overage charges" />
                <Field label={`${localPriceCurrency}/km overage`} type="number" testid="ev-overage" v={form.overage_fee_per_km} on={(v) => setForm((f) => ({ ...f, overage_fee_per_km: v }))} hint="Charged per km above the daily allowance" />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-[var(--zc-text-2)] mb-3">Operating hours</div>
              <p className="text-xs text-[var(--zc-text-3)] mb-3">Set when your vehicle is available for pick-up and return. Outside these hours an after-hours fee applies. Leave blank to use the default (10 pm – 6 am window, $25 fee).</p>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Open (HH:MM)" testid="ev-op-open" v={form.operating_open} on={(v) => setForm((f) => ({ ...f, operating_open: v }))} placeholder="08:00" hint="e.g. 08:00" />
                <Field label="Close (HH:MM)" testid="ev-op-close" v={form.operating_close} on={(v) => setForm((f) => ({ ...f, operating_close: v }))} placeholder="18:00" hint="e.g. 18:00" />
                <Field label={`After-hours fee (${localPriceCurrency})`} type="number" testid="ev-ah-fee" v={form.after_hours_fee} on={(v) => setForm((f) => ({ ...f, after_hours_fee: v }))} placeholder="25" hint="Fee per after-hours slot" />
              </div>
              {form.operating_open && form.operating_close && (
                <p className="text-xs text-[var(--zc-text-3)] mt-3">
                  Available {form.operating_open} – {form.operating_close} daily.
                  {form.after_hours_fee ? ` After-hours fee: ${localPriceCurrency === "CAD" ? "CA$" : "$"}${form.after_hours_fee}` : " After-hours fee: $25 (default)"}
                </p>
              )}
            </div>
          </div>
        </div>

        <div>
          <button type="button" onClick={aiSuggest} disabled={suggesting} className="inline-flex items-center gap-2 px-4 h-10 zc-btn-outline-secondary text-xs" data-testid="ev-ai-suggest">
            <Sparkles size={12} /> {suggesting ? `Asking ${AI_NAME}…` : "AI suggest a fair price"}
          </button>
          {suggestion && (
            <div className="mt-3 p-5 rounded-lg zc-card" data-testid="ev-ai-out">
              <div className="eyebrow">Suggested rate</div>
              <div className="zc-price text-2xl mt-1">{formatMoney(suggestion.suggested_rate, suggestionCurrency)}<span className="zc-unit normal-case tracking-normal">/day</span></div>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs text-[var(--zc-text-3)]">Description</label>
          <textarea className="zc-input w-full p-3 text-sm h-28 mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="ev-desc" />
        </div>

        <div>
          <label className="text-xs text-[var(--zc-text-3)]">Features (comma separated)</label>
          <input className="zc-input w-full h-11 px-3 text-sm mt-1" value={form.features} onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))} data-testid="ev-features" />
        </div>

        <div>
          <label className="text-xs text-[var(--zc-text-3)] block mb-2">
            {status === "rejected" ? "Listing photos (required for resubmit)" : "Photos"}
          </label>
          {status === "rejected" ? (
            <p className="zc-meta mb-2">
              Add clear exterior shots — front, rear, and both sides. These are reviewed with your AI condition baseline.
            </p>
          ) : null}
          <ImageUploader value={form.images} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} max={4} testid="ev-uploader" />
        </div>

        <HostCalendarPanel vehicleId={id} />

        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--zc-border)]">
          <button type="button" onClick={() => navigate("/host?tab=cars")} className="px-4 h-11 zc-btn-ghost text-sm" data-testid="ev-cancel">Cancel</button>
          {status === "rejected" ? (
            <button type="button" disabled={submitting} onClick={(e) => submit(e, true)} className="px-5 h-11 zc-btn-primary text-sm flex items-center gap-2" data-testid="ev-resubmit">
              <Save size={14} /> {submitting ? "Saving…" : "Save & resubmit"}
            </button>
          ) : (
            <button type="submit" disabled={submitting} className="px-5 h-11 zc-btn-primary text-sm flex items-center gap-2" data-testid="ev-submit">
              <Save size={14} /> {submitting ? "Saving…" : "Save changes"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, v, on, type = "text", required, testid, placeholder, hint, mono, maxLength, readOnly, onBlur }) {
  return (
    <div>
      <label className="text-xs text-[var(--zc-text-3)]">{label}</label>
      <input
        type={type}
        required={required}
        readOnly={readOnly}
        placeholder={placeholder}
        maxLength={maxLength}
        className={"zc-input w-full h-11 px-3 text-sm mt-1 " + (mono ? "font-mono uppercase tracking-wide" : "") + (readOnly ? " bg-[var(--zc-bg-2)] text-[var(--zc-text-2)]" : "")}
        value={v}
        onChange={(e) => on(e.target.value)}
        onBlur={onBlur}
        data-testid={testid}
      />
      {hint && <div className="zc-meta mt-1">{hint}</div>}
    </div>
  );
}

function SelectField({ label, v, on, options, testid }) {
  return (
    <div>
      <label className="text-xs text-[var(--zc-text-3)]">{label}</label>
      <select className="zc-input w-full h-11 px-3 text-sm mt-1" value={v} onChange={(e) => on(e.target.value)} data-testid={testid}>
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}

function PolicyCheckbox({ checked, onChange, testid, label, hint }) {
  return (
    <label className="flex items-start gap-3 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[var(--zc-accent)]" data-testid={testid} />
      <span>
        <span className="block text-[var(--zc-text)]">{label}</span>
        <span className="zc-meta block mt-0.5">{hint}</span>
      </span>
    </label>
  );
}
